var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useState } from 'react';
import { useAddCardMutation } from '../useAddCardForTeamAccountMutation';
import { print as printGql } from 'graphql/language/printer';
import { useExperience } from '@doordash/component-experience';
import { Locale } from '@doordash/lib-i18n';
import { readCookie } from '../../../../utils/cookie_helper';
import { getTokenizedVgsDataQuery } from './GetTokenizedVgsData';
import { VgsFormFieldName, } from './Types/vgsCollect';
import useGenerateVgsFormEffect from './useGenerateVgsFormEffect';
import useVgsPaymentConfig from './useVgsPaymentConfig';
import PaymentComponentStrings from '../PaymentComponents/PaymentComponentStrings';
import { usePaymentConfigContext } from '../PaymentComponents/PaymentConfigContext';
var CHANNEL_MARKETPLACE = 'marketplace';
var ERROR_STRINGS = PaymentComponentStrings.cardFields.errors;
var VGS_VALIDATION_ERRORS = new Map([
    ['is required', ERROR_STRINGS.requiredField()],
    ['is not a valid expiration date', ERROR_STRINGS.invalidField()],
    ['is not a valid card number', ERROR_STRINGS.invalidField()],
    ['is not a valid security code', ERROR_STRINGS.invalidField()],
    ['default', ERROR_STRINGS.invalidField()],
]);
var handleSubmitSuccess = function (onSuccess, onError, addPaymentCard, teamAccountId, payerData, paymentConfigToken) { return function (status, response) {
    var _a, _b;
    // Handle server side errors.
    if (status !== 200) {
        var message = JSON.stringify(response);
        onError({
            inlineErrors: {},
            globalError: {
                displayMsg: ERROR_STRINGS.addCard(),
                reason: 'vgs_failure',
                message: message,
                originalException: response,
            },
        });
        return;
    }
    var cardData = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.getTokenizedVgsData;
    if (!cardData) {
        var apolloError = (_b = response === null || response === void 0 ? void 0 : response.errors) === null || _b === void 0 ? void 0 : _b[0];
        onError({
            inlineErrors: {},
            globalError: {
                displayMsg: (apolloError === null || apolloError === void 0 ? void 0 : apolloError.message) || ERROR_STRINGS.addCard(),
                reason: 'vgs_failure',
                message: (apolloError === null || apolloError === void 0 ? void 0 : apolloError.message) ||
                    "" + apolloError ||
                    'Unknown error occurred getting tokenized VGS card data',
                originalException: response,
            },
        });
        return;
    }
    addPaymentCard({
        variables: {
            teamAccountId: teamAccountId,
            tokenizationProvider: 'vgs',
            stripeToken: '',
            cardData: cardData,
            payerData: payerData,
            paymentConfigToken: paymentConfigToken,
        },
    })
        .then(function (result) {
        var _a;
        if ((_a = result.errors) === null || _a === void 0 ? void 0 : _a[0]) {
            var e = result.errors[0];
            return onError({
                inlineErrors: {},
                globalError: {
                    displayMsg: ERROR_STRINGS.addCard(),
                    reason: 'vgs_failure',
                    message: e.name + ": " + e.message,
                    originalException: result,
                },
            });
        }
        return onSuccess();
    })
        .catch(function (e) {
        var _a, _b, _c;
        var message = ((_b = (_a = e.graphQLErrors[0].extensions) === null || _a === void 0 ? void 0 : _a.responseData) === null || _b === void 0 ? void 0 : _b.error_message) || ((_c = e.networkError) === null || _c === void 0 ? void 0 : _c.message) ||
            e.message ||
            JSON.stringify(e);
        onError({
            inlineErrors: {},
            globalError: {
                displayMsg: ERROR_STRINGS.addCard(),
                reason: 'vgs_failure',
                message: message,
                originalException: e,
            },
        });
    });
}; };
var handleSubmitError = function (onError) { return function (vgsResponseErrors) {
    var inlineErrors = {};
    Object.keys(VgsFormFieldName).forEach(function (fieldName) {
        var _a, _b;
        var field = VgsFormFieldName[fieldName];
        var errMsg = (_b = (_a = vgsResponseErrors[field]) === null || _a === void 0 ? void 0 : _a.errorMessages) === null || _b === void 0 ? void 0 : _b[0];
        var i18nErrString = VGS_VALIDATION_ERRORS.get(errMsg) || VGS_VALIDATION_ERRORS.get('default');
        inlineErrors[field] = i18nErrString;
    });
    onError({ inlineErrors: inlineErrors, globalError: undefined });
}; };
export default function useVgsForm(isVgsLoaded, teamAccountId) {
    var paymentConfigContext = usePaymentConfigContext();
    var vgsConfig = useVgsPaymentConfig().vgsConfig;
    var _a = __read(useState(null), 2), form = _a[0], setForm = _a[1];
    var _b = __read(useState({ last4: 0, iin: 0 }), 2), cardData = _b[0], setCardData = _b[1];
    var _c = __read(useAddCardMutation(teamAccountId), 1), addPaymentCard = _c[0];
    var experience = useExperience().experience;
    var payerData = {};
    useGenerateVgsFormEffect({ isVgsLoaded: isVgsLoaded, vgsConfig: vgsConfig, setForm: setForm, setCardData: setCardData });
    var formSubmit = function (onSuccess, onError) {
        if (!form || !form.submit) {
            throw new Error('VGS vendor form is not initialized');
        }
        return form.submit(vgsConfig.SUBMIT_URL, {
            mapDotToObject: 'merge',
            data: {
                operationName: 'getTokenizedVgsData',
                query: printGql(getTokenizedVgsDataQuery),
                variables: { cardData: cardData },
            },
            headers: {
                'Accept-Language': Locale.EN_US,
                'X-Experience-Id': experience,
                'X-Channel-Id': CHANNEL_MARKETPLACE,
                'X-CSRFToken': readCookie('csrf_token') || '',
            },
        }, handleSubmitSuccess(onSuccess, onError, addPaymentCard, teamAccountId, payerData, paymentConfigContext === null || paymentConfigContext === void 0 ? void 0 : paymentConfigContext.paymentConfigToken), handleSubmitError(onError));
    };
    return {
        form: form,
        formSubmit: formSubmit,
        formData: {},
    };
}
